/** `/connect/wordpress`: "Connect to Solar OS" from the WordPress plugin. The plugin sends its admin here with
 * `site`, `return`, `state` and `name`; the user picks a company and an embed key (or a new one), the API issues a
 * single-use code and we navigate back to the plugin, which exchanges it server to server. */
import { useQuery } from "@tanstack/react-query";
import { Building2, Check, KeyRound, Plug, Plus } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg } from "../api/org";
import { Alert, Button, Select, Spinner } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";

const ORIGIN = /^https?:\/\/[a-z0-9.-]+(:[0-9]{1,5})?$/;

function originOf(url: string): string | null {
  try {
    const parsed = new URL(url);
    return /^https?:$/.test(parsed.protocol) ? parsed.origin : null;
  } catch {
    return null;
  }
}

function withParams(url: string, params: Record<string, string>): string {
  const target = new URL(url);
  for (const [k, v] of Object.entries(params)) target.searchParams.set(k, v);
  return target.toString();
}

function maskKey(key: string): string {
  const prefix = key.startsWith("pk_test_") ? "pk_test_" : "pk_live_";
  return `${prefix}…${key.slice(-4)}`;
}

function siteMatches(key: S["EmbedKey"], site: string): boolean {
  return key.allowed_origins.some((entry) => {
    if (entry === site) return true;
    const [scheme, rest] = entry.split("://");
    return rest?.startsWith("*.") && site.startsWith(`${scheme}://`) && site.endsWith(rest.slice(1));
  });
}

export function ConnectWordPress() {
  const [params] = useSearchParams();
  const t = useT();
  const intl = useIntlShape();
  const b = (c: ReactNode[]) => <b>{c}</b>;
  const { memberships, me, isPlatformAdmin } = useOrg();
  const site = (params.get("site") ?? "").toLowerCase().replace(/\/+$/, "");
  const returnUrl = params.get("return") ?? "";
  const state = params.get("state") ?? "";
  const siteName = params.get("name") ?? "";
  const host = useMemo(() => site.replace(/^https?:\/\//, ""), [site]);
  const valid = ORIGIN.test(site) && originOf(returnUrl) === site && /^[A-Za-z0-9_-]{8,200}$/.test(state);

  const manageable = memberships.filter((m) => isPlatformAdmin || m.permissions.includes("integrations:create"));
  const [orgId, setOrgId] = useState<string>(manageable[0]?.organization_id ?? "");
  const keys = useQuery({
    queryKey: ["embed-keys", orgId],
    enabled: !!orgId,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/embed-keys", { params: { path: { org_id: orgId } } })),
  });
  const activeKeys = useMemo(() => (keys.data?.items ?? []).filter((k) => k.active), [keys.data]);
  const [choice, setChoice] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Create a new key for this site" is the default; existing keys stay selectable.
  useEffect(() => {
    setChoice("new");
  }, [orgId]);

  function cancel() {
    if (valid) window.location.assign(withParams(returnUrl, { error: "cancelled", state }));
    else window.history.back();
  }

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const result = await unwrap(
        api.POST("/v1/orgs/{org_id}/wordpress-connect", {
          params: { path: { org_id: orgId } },
          body: {
            site,
            return_url: returnUrl,
            state,
            ...(choice === "new" ? { create_new: true } : { embed_key_id: choice }),
          },
        }),
      );
      window.location.assign(result.redirect_url);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  const chosen = activeKeys.find((k) => k.id === choice);
  const alreadyAllowed = chosen ? siteMatches(chosen, site) : false;

  return (
    <div className="connect-page">
      <div className="connect-card card">
        <div className="connect-head">
          <div className="connect-logos" aria-hidden>
            <span className="connect-logo wp">W</span>
            <span className="connect-dots" />
            <img className="connect-logo" src="/solaros-mark.png" alt="" />
          </div>
          <h1>
            {intl.formatMessage(
              { id: "website.connect.title" },
              { site: (c) => <span className="connect-site">{c}</span>, name: siteName || host || t("website.connect.yourSite") },
            )}
          </h1>
          {valid && <p className="muted mono small" dir="ltr">{site}</p>}
        </div>

        {!valid ? (
          <Alert tone="danger" title={t("website.connect.invalid.title")}>
            {intl.formatMessage({ id: "website.connect.invalid.body" }, { b })}
          </Alert>
        ) : manageable.length === 0 ? (
          <Alert tone="warn" title={t("website.connect.cannot.title")}>
            {memberships.length
              ? t("website.connect.cannot.askAdmin")
              : intl.formatMessage(
                  { id: "website.connect.cannot.noCompany" },
                  { b: (c) => <b dir="ltr">{c}</b>, email: me.user.email, link: (c) => <a href="/onboarding">{c}</a> },
                )}
          </Alert>
        ) : (
          <div className="stack" style={{ gap: 18 }}>
            {error && <Alert tone="danger">{error}</Alert>}
            <div className="stack" style={{ gap: 6 }}>
              <span className="connect-label"><Building2 size={14} /> {t("website.connect.company")}</span>
              {manageable.length === 1 ? (
                <div className="connect-static">{manageable[0].organization_name}</div>
              ) : (
                <Select
                  value={orgId}
                  onChange={(v) => setOrgId(v)}
                  options={manageable.map((m) => ({ value: m.organization_id, label: m.organization_name }))}
                />
              )}
            </div>

            <div className="stack" style={{ gap: 6 }}>
              <span className="connect-label"><KeyRound size={14} /> {t("website.install.embedKey")}</span>
              {keys.isLoading ? (
                <Spinner label={t("website.connect.loadingKeys")} />
              ) : keys.error ? (
                <Alert tone="danger">{errorMessage(keys.error)}</Alert>
              ) : (
                <div className="connect-choices" role="radiogroup">
                  {activeKeys.map((k) => (
                    <label key={k.id} className={`connect-choice${choice === k.id ? " on" : ""}`}>
                      <input type="radio" name="key" checked={choice === k.id} onChange={() => setChoice(k.id)} />
                      <span className="grow">
                        <b>{k.label || t("website.key.untitled")}</b>
                        <span className="muted small mono" dir="ltr"> {maskKey(k.key)}</span>
                        <span className="faint small" style={{ display: "block" }}>
                          {t("website.connect.websites", { count: k.allowed_origins.length })}
                          {siteMatches(k, site) ? <> · {t("website.connect.alreadyAllows")}</> : null}
                        </span>
                      </span>
                      {choice === k.id && <Check size={16} className="connect-tick" />}
                    </label>
                  ))}
                  <label className={`connect-choice${choice === "new" ? " on" : ""}`}>
                    <input type="radio" name="key" checked={choice === "new"} onChange={() => setChoice("new")} />
                    <span className="grow">
                      <b><Plus size={13} style={{ verticalAlign: -2 }} /> {t("website.connect.newKey")}</b>
                      <span className="faint small" style={{ display: "block" }}>{t("website.connect.newKeyName", { name: `WordPress · ${host}` })}</span>
                    </span>
                    {choice === "new" && <Check size={16} className="connect-tick" />}
                  </label>
                </div>
              )}
            </div>

            <p className="small muted connect-note">
              {intl.formatMessage({ id: alreadyAllowed ? "website.connect.note.allowed" : "website.connect.note.added" }, { b: (c) => <b dir="ltr">{c}</b>, host })}
            </p>

            <div className="connect-actions">
              <Button variant="ghost" onClick={cancel} disabled={busy}>{t("website.action.cancel")}</Button>
              <Button variant="primary" size="lg" icon={<Plug />} loading={busy} disabled={!orgId || !choice || keys.isLoading} onClick={() => void connect()}>
                {t("website.connect.connect")}
              </Button>
            </div>
          </div>
        )}
        {!valid && (
          <div className="connect-actions">
            <Button variant="ghost" onClick={cancel}>{t("website.connect.goBack")}</Button>
          </div>
        )}
      </div>
      <p className="faint small" style={{ textAlign: "center" }}>{t("website.connect.signedInAs", { email: me.user.email })}</p>
    </div>
  );
}
